/**
 * Airside vehicles, drawn from above the way they look from above.
 *
 * Every vehicle is laid out along its own length: the front at the top
 * (y = -8), the rear at the bottom (y = +8), sixteen sprite units long — the
 * same unit the map's `vehicleScale` sizes against the vehicle's real length,
 * so a foam tender is twice a patrol car and both sit on a runway at their
 * true size when zoomed in. The width comes from the vehicle's real width and
 * length, not from a fixed box.
 *
 * What makes each one read as itself at a glance, from a control-room
 * distance: the cab and windscreen at the front, the headlights ahead of it
 * and the tail lights behind, the tyres at the axles, the shading that turns
 * a flat rectangle into a roof with sides — and then the thing that is that
 * vehicle's alone: the roof monitor and ladder of a tender, the light bar of
 * a patrol car, the box of a lighting van, the bed of a pickup, the load on
 * a transporter, the brush head of a sweeper.
 */

type G = CanvasRenderingContext2D;

export type VehicleKind = "fire" | "patrol" | "van" | "truck" | "transporter" | "pickup" | "sweeper";

export interface VehicleOpts {
  /** real length and width in metres, for the proportions */
  lenM?: number;
  widM?: number;
  /** moving: headlights on and throwing light ahead */
  moving?: boolean;
}

const L = 16;
const FRONT = -L / 2;

function rr(g: G, x: number, y: number, w: number, h: number, r: number) {
  g.beginPath();
  if (g.roundRect) g.roundRect(x, y, w, h, Math.min(r, w / 2, h / 2));
  else g.rect(x, y, w, h);
}

/** a side-lit panel: darker at the edges, lighter along the middle, which is what a roof looks like */
function shade(g: G, W: number, s: number, base: string, light: string) {
  const grd = g.createLinearGradient((-W / 2) * s, 0, (W / 2) * s, 0);
  grd.addColorStop(0, base);
  grd.addColorStop(0.5, light);
  grd.addColorStop(1, base);
  return grd;
}

function tyres(g: G, W: number, s: number, axles: number[]) {
  g.fillStyle = "#101214";
  for (const y of axles)
    for (const side of [-1, 1]) {
      rr(g, (side * (W / 2 + 0.15) - 0.75) * s, (y - 1.25) * s, 1.5 * s, 2.5 * s, 0.5 * s);
      g.fill();
    }
}

function mirrors(g: G, W: number, s: number, y: number) {
  g.fillStyle = "#1A1D21";
  for (const side of [-1, 1]) {
    rr(g, (side * (W / 2 + 0.55) - 0.55) * s, (y - 0.35) * s, 1.1 * s, 0.7 * s, 0.25 * s);
    g.fill();
  }
}

function lights(g: G, W: number, s: number, moving: boolean) {
  /* the beams, only when it is driving — how a moving vehicle is told from a parked one at night */
  if (moving) {
    for (const side of [-1, 1]) {
      const x = side * (W / 2 - 0.9) * s;
      const grd = g.createLinearGradient(x, FRONT * s, x, (FRONT - 7) * s);
      grd.addColorStop(0, "rgba(255,244,210,.34)");
      grd.addColorStop(1, "rgba(255,244,210,0)");
      g.beginPath();
      g.moveTo(x - 0.6 * s, FRONT * s);
      g.lineTo(x - 2.2 * s, (FRONT - 7) * s);
      g.lineTo(x + 2.2 * s, (FRONT - 7) * s);
      g.lineTo(x + 0.6 * s, FRONT * s);
      g.closePath();
      g.fillStyle = grd;
      g.fill();
    }
  }
  /* headlights at the front corners, tail lights at the back */
  for (const side of [-1, 1]) {
    g.beginPath();
    g.ellipse(side * (W / 2 - 0.9) * s, (FRONT + 0.35) * s, 0.75 * s, 0.35 * s, 0, 0, 6.284);
    g.fillStyle = "#FFF6DA";
    g.fill();
    rr(g, (side * (W / 2 - 0.9) - 0.6) * s, (L / 2 - 0.55) * s, 1.2 * s, 0.45 * s, 0.15 * s);
    g.fillStyle = moving ? "#E0231B" : "#8E1712";
    g.fill();
  }
}

/** the windscreen: a dark band of glass, wider at the base, with a sky reflection */
function windscreen(g: G, W: number, s: number, y: number, h: number) {
  const w0 = W - 1.2,
    w1 = W - 2.0;
  g.beginPath();
  g.moveTo((-w1 / 2) * s, y * s);
  g.lineTo((w1 / 2) * s, y * s);
  g.lineTo((w0 / 2) * s, (y + h) * s);
  g.lineTo((-w0 / 2) * s, (y + h) * s);
  g.closePath();
  const grd = g.createLinearGradient(0, y * s, 0, (y + h) * s);
  grd.addColorStop(0, "#5E7C92");
  grd.addColorStop(1, "#1B2833");
  g.fillStyle = grd;
  g.fill();
}

function body(g: G, W: number, s: number, y: number, h: number, base: string, light: string, r = 1.1) {
  rr(g, (-W / 2) * s, y * s, W * s, h * s, r * s);
  g.fillStyle = shade(g, W, s, base, light);
  g.fill();
  g.strokeStyle = "rgba(6,8,10,.85)";
  g.lineWidth = 0.35 * s;
  g.stroke();
}

function beacon(g: G, s: number, x: number, y: number, t: number, colour: string, phase = 0) {
  const on = Math.sin(t * 10 + phase) > 0.2;
  g.beginPath();
  g.arc(x * s, y * s, (on ? 0.95 : 0.6) * s, 0, 6.284);
  g.fillStyle = on ? colour : "rgba(90,90,90,.9)";
  g.fill();
  if (on) {
    g.beginPath();
    g.arc(x * s, y * s, 3.2 * s, 0, 6.284);
    const grd = g.createRadialGradient(x * s, y * s, 0, x * s, y * s, 3.2 * s);
    grd.addColorStop(0, colour + "66");
    grd.addColorStop(1, colour + "00");
    g.fillStyle = grd;
    g.fill();
  }
}

/** a light bar across the roof, alternating its two colours */
function lightBar(g: G, W: number, s: number, y: number, t: number, a: string, b: string) {
  rr(g, (-(W - 1.6) / 2) * s, (y - 0.45) * s, (W - 1.6) * s, 0.9 * s, 0.4 * s);
  g.fillStyle = "#23272C";
  g.fill();
  const flip = Math.sin(t * 9) > 0;
  beacon(g, s, -(W - 2.6) / 2, y, t, flip ? a : "#303030");
  beacon(g, s, (W - 2.6) / 2, y, t + 0.35, flip ? "#303030" : b);
}

function shadow(g: G, W: number, s: number) {
  rr(g, (-W / 2 + 0.7) * s, (FRONT + 0.9) * s, W * s, L * s, 1.4 * s);
  g.fillStyle = "rgba(0,0,0,.35)";
  g.fill();
}

function widthOf(o: VehicleOpts, fallback: number): number {
  if (!o.lenM || !o.widM) return fallback;
  return Math.max(5.4, Math.min(8.6, (L * o.widM) / o.lenM));
}

export function drawRealVehicle(g: G, s: number, kind: VehicleKind, t: number, o: VehicleOpts = {}): void {
  const moving = !!o.moving;
  switch (kind) {
    case "fire": {
      /* the foam tender: a long red truck, the cab well forward, the roof monitor and the ladder behind it */
      const W = widthOf(o, 7.4);
      shadow(g, W, s);
      tyres(g, W, s, [-4.6, 2.2, 5.4]);
      lights(g, W, s, moving);
      body(g, W, s, FRONT, L, "#9E140F", "#E0281F", 1.2);
      windscreen(g, W, s, FRONT + 0.9, 1.9);
      mirrors(g, W, s, FRONT + 1.4);
      /* the cab roof and its bar of red lights */
      rr(g, (-(W - 1.2) / 2) * s, (FRONT + 2.9) * s, (W - 1.2) * s, 2.6 * s, 0.6 * s);
      g.fillStyle = "#C21E17";
      g.fill();
      lightBar(g, W, s, FRONT + 3.3, t, "#FF3B30", "#FF3B30");
      /* the white stripe down each side */
      g.fillStyle = "#F2F2F2";
      for (const side of [-1, 1]) {
        rr(g, (side * (W / 2 - 0.55) - 0.25) * s, (FRONT + 6) * s, 0.5 * s, 9.2 * s, 0.2 * s);
        g.fill();
      }
      /* the roof monitor: a turret and its nozzle, pointing ahead */
      g.beginPath();
      g.arc(0, (FRONT + 7.2) * s, 1.25 * s, 0, 6.284);
      g.fillStyle = "#BFC4C9";
      g.fill();
      rr(g, -0.3 * s, (FRONT + 4.6) * s, 0.6 * s, 2.6 * s, 0.25 * s);
      g.fillStyle = "#8E959C";
      g.fill();
      /* the ladder on the rear roof */
      g.strokeStyle = "#C9CED3";
      g.lineWidth = 0.3 * s;
      for (const x of [-1.1, 1.1]) {
        g.beginPath();
        g.moveTo(x * s, (FRONT + 9.2) * s);
        g.lineTo(x * s, (L / 2 - 1) * s);
        g.stroke();
      }
      for (let y = FRONT + 9.6; y < L / 2 - 1; y += 0.9) {
        g.beginPath();
        g.moveTo(-1.1 * s, y * s);
        g.lineTo(1.1 * s, y * s);
        g.stroke();
      }
      break;
    }
    case "patrol": {
      /* the patrol car: bonnet, windscreen, roof with the light bar, rear window, boot */
      const W = widthOf(o, 6.2);
      shadow(g, W, s);
      tyres(g, W, s, [-4.6, 4.8]);
      lights(g, W, s, moving);
      body(g, W, s, FRONT, L, "#15304F", "#2A5487", 2.2);
      /* the white doors band */
      rr(g, (-W / 2) * s, (FRONT + 5.2) * s, W * s, 5.4 * s, 0.4 * s);
      g.fillStyle = shade(g, W, s, "#C9CFD5", "#F4F6F8");
      g.fill();
      windscreen(g, W, s, FRONT + 4.0, 2.1);
      mirrors(g, W, s, FRONT + 4.6);
      /* the roof */
      rr(g, (-(W - 1.5) / 2) * s, (FRONT + 6.1) * s, (W - 1.5) * s, 4.2 * s, 0.9 * s);
      g.fillStyle = shade(g, W - 1.5, s, "#D5DADF", "#FFFFFF");
      g.fill();
      lightBar(g, W, s, FRONT + 7.2, t, "#2F7BFF", "#FF3B30");
      /* the rear window */
      rr(g, (-(W - 2) / 2) * s, (FRONT + 10.4) * s, (W - 2) * s, 1.4 * s, 0.4 * s);
      g.fillStyle = "#1E2B36";
      g.fill();
      break;
    }
    case "van": {
      /* the lighting van: short bonnet, cab, and a tall box behind with its roof rack */
      const W = widthOf(o, 6.6);
      shadow(g, W, s);
      tyres(g, W, s, [-4.9, 4.6]);
      lights(g, W, s, moving);
      body(g, W, s, FRONT, L, "#B99312", "#F0CB2A", 1.2);
      windscreen(g, W, s, FRONT + 1.3, 2.0);
      mirrors(g, W, s, FRONT + 1.9);
      /* the box roof and the rack of ladders and cable reels on it */
      rr(g, (-(W - 0.8) / 2) * s, (FRONT + 3.8) * s, (W - 0.8) * s, 11.6 * s, 0.6 * s);
      g.fillStyle = shade(g, W - 0.8, s, "#D9B41E", "#F7DC5A");
      g.fill();
      g.strokeStyle = "rgba(60,50,10,.55)";
      g.lineWidth = 0.28 * s;
      for (let y = FRONT + 5; y < L / 2 - 1; y += 1.5) {
        g.beginPath();
        g.moveTo((-(W - 1.8) / 2) * s, y * s);
        g.lineTo(((W - 1.8) / 2) * s, y * s);
        g.stroke();
      }
      beacon(g, s, 0, FRONT + 3.4, t, "#FF9614");
      break;
    }
    case "pickup": {
      /* the pickup: bonnet, cab, and the open bed that makes it a pickup */
      const W = widthOf(o, 6.0);
      shadow(g, W, s);
      tyres(g, W, s, [-4.4, 4.6]);
      lights(g, W, s, moving);
      body(g, W, s, FRONT, L, "#B9BEC4", "#F1F3F5", 1.8);
      windscreen(g, W, s, FRONT + 3.6, 2.0);
      mirrors(g, W, s, FRONT + 4.2);
      /* the cab roof and rear window */
      rr(g, (-(W - 1.4) / 2) * s, (FRONT + 5.6) * s, (W - 1.4) * s, 3.4 * s, 0.8 * s);
      g.fillStyle = shade(g, W - 1.4, s, "#CDD2D7", "#FFFFFF");
      g.fill();
      rr(g, (-(W - 1.9) / 2) * s, (FRONT + 9) * s, (W - 1.9) * s, 0.8 * s, 0.3 * s);
      g.fillStyle = "#1E2B36";
      g.fill();
      /* the bed: dark, with its floor ribs and the tailgate */
      rr(g, (-(W - 1.2) / 2) * s, (FRONT + 10.2) * s, (W - 1.2) * s, 5.2 * s, 0.4 * s);
      g.fillStyle = "#2B3035";
      g.fill();
      g.strokeStyle = "rgba(255,255,255,.12)";
      g.lineWidth = 0.25 * s;
      for (const x of [-1.2, 0, 1.2]) {
        g.beginPath();
        g.moveTo(x * s, (FRONT + 10.6) * s);
        g.lineTo(x * s, (FRONT + 15) * s);
        g.stroke();
      }
      beacon(g, s, 0, FRONT + 6, t, "#FF9614", 0.7);
      break;
    }
    case "truck":
    case "transporter": {
      /* the crew truck or the plant transporter: a cab, then a flatbed carrying what it carries */
      const W = widthOf(o, 7.0);
      shadow(g, W, s);
      tyres(g, W, s, kind === "transporter" ? [-4.8, 3.4, 5.6] : [-4.6, 4.8]);
      lights(g, W, s, moving);
      /* the cab */
      body(g, W, s, FRONT, 4.4, "#BFC4CA", "#F4F6F8", 1.2);
      windscreen(g, W, s, FRONT + 0.8, 1.7);
      mirrors(g, W, s, FRONT + 1.3);
      beacon(g, s, 0, FRONT + 3.3, t, "#FF9614");
      /* the flatbed */
      rr(g, (-W / 2) * s, (FRONT + 4.8) * s, W * s, 11.2 * s, 0.5 * s);
      g.fillStyle = shade(g, W, s, "#50565C", "#6F767D");
      g.fill();
      g.strokeStyle = "rgba(6,8,10,.8)";
      g.lineWidth = 0.35 * s;
      g.stroke();
      if (kind === "transporter") {
        /* a small excavator chained on the bed: tracks, the cab and the arm folded forward */
        g.fillStyle = "#1E2226";
        for (const x of [-1.9, 1.9]) {
          rr(g, (x - 0.7) * s, (FRONT + 8) * s, 1.4 * s, 6.4 * s, 0.5 * s);
          g.fill();
        }
        rr(g, -2 * s, (FRONT + 8.8) * s, 4 * s, 4.4 * s, 0.8 * s);
        g.fillStyle = shade(g, 4, s, "#C99A10", "#F5C518");
        g.fill();
        rr(g, -0.5 * s, (FRONT + 5.6) * s, 1 * s, 3.6 * s, 0.4 * s);
        g.fillStyle = "#E0B016";
        g.fill();
      } else {
        /* tool boxes and a cable drum */
        g.fillStyle = "#8A6A2E";
        rr(g, (-(W - 1.4) / 2) * s, (FRONT + 5.6) * s, 2.4 * s, 3 * s, 0.3 * s);
        g.fill();
        rr(g, ((W - 1.4) / 2 - 2.4) * s, (FRONT + 5.6) * s, 2.4 * s, 3 * s, 0.3 * s);
        g.fill();
        g.beginPath();
        g.arc(0, (FRONT + 12) * s, 1.8 * s, 0, 6.284);
        g.fillStyle = "#3D6E9E";
        g.fill();
        g.beginPath();
        g.arc(0, (FRONT + 12) * s, 0.6 * s, 0, 6.284);
        g.fillStyle = "#1B2833";
        g.fill();
      }
      break;
    }
    case "sweeper": {
      /* the runway sweeper: the brush head out in front, the cab, the hopper behind */
      const W = widthOf(o, 7.2);
      shadow(g, W, s);
      tyres(g, W, s, [-3.2, 5]);
      lights(g, W, s, moving);
      /* the pick-up head and the two gutter brooms, turning */
      rr(g, (-(W + 1.6) / 2) * s, (FRONT - 1.6) * s, (W + 1.6) * s, 1.8 * s, 0.6 * s);
      g.fillStyle = "#3C3C3C";
      g.fill();
      const sp = t * 7;
      for (const [x, dir] of [
        [-(W / 2 + 0.4), 1],
        [W / 2 + 0.4, -1],
      ] as const) {
        g.save();
        g.translate(x * s, (FRONT - 0.7) * s);
        g.rotate(sp * dir);
        g.strokeStyle = "#9C8A62";
        g.lineWidth = 0.35 * s;
        for (let k = 0; k < 8; k++) {
          g.beginPath();
          g.moveTo(0, 0);
          g.lineTo(Math.cos((k / 8) * 6.284) * 1.7 * s, Math.sin((k / 8) * 6.284) * 1.7 * s);
          g.stroke();
        }
        g.restore();
      }
      body(g, W, s, FRONT, L, "#C6771C", "#F29A2E", 1.2);
      windscreen(g, W, s, FRONT + 0.8, 2.1);
      mirrors(g, W, s, FRONT + 1.4);
      /* the hopper and its lid seam */
      rr(g, (-(W - 1) / 2) * s, (FRONT + 5.6) * s, (W - 1) * s, 9.4 * s, 0.8 * s);
      g.fillStyle = shade(g, W - 1, s, "#D9831F", "#FFB043");
      g.fill();
      g.strokeStyle = "rgba(60,30,5,.5)";
      g.lineWidth = 0.3 * s;
      g.beginPath();
      g.moveTo((-(W - 1) / 2) * s, (FRONT + 10) * s);
      g.lineTo(((W - 1) / 2) * s, (FRONT + 10) * s);
      g.stroke();
      beacon(g, s, 0, FRONT + 4.4, t, "#FF9614");
      /* the dust it lifts behind, which is how you know it is working */
      if (moving) {
        g.beginPath();
        g.arc(0, (L / 2 + 2) * s, (3.4 + Math.sin(t * 4) * 0.5) * s, 0, 6.284);
        g.fillStyle = "rgba(190,170,130,.2)";
        g.fill();
      }
      break;
    }
  }
}
